import { fromEvent as observableFromEvent } from 'rxjs';
import { catchError, map, distinctUntilChanged, debounceTime } from 'rxjs/operators';
import { NgModule } from '@angular/core';
import { APP_BASE_HREF, CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { HttpModule, JsonpModule } from '@angular/http';

import { AppModule } from './app.module';
import { AppComponent } from './app.component';
import { RouterModule } from '@angular/router';
import { Config } from './universal-config';
import { Meta } from '../angular2-meta';
import { BrowserTransferStateModule } from '../modules/transfer-state/browser-transfer-state.module';
import { TransferState } from '../modules/transfer-state/transfer-state';

import { StoreModule, Store, INITIAL_STATE } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { IAppState } from './app.state';
import { IVisualState } from './visual/visual.reducer';
import { SocketIoService } from './profile/socket.io.service';

import { reducers } from './reducers';
import {
  setIsHeaderFixed,
  showToTopComponent,
  setMobile,
  setWindowSize
} from './visual/visual.actions';

import { OrigamiModule } from '@codebakery/origami';

import { EffectsModule } from '@ngrx/effects';
import { ShareEffects } from './share/share.effects';
import { ProfileEffects } from './profile/profile.effects';
import { OrganizationsEffects } from './organizations/organizations.effects';
import { WorkspacesEffects } from './workspaces/workspaces.effects';
import { AddThisService } from './common/addthis.service';

const HEADER_SCROLL_HEIGHT = 80;
const MOBILE_WIDTH = 600;
const TABLET_WIDTH = 768;

export function getState(cache: TransferState) {
  return cache.get('NGRX_state');
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    StoreModule.forRoot(reducers),
    EffectsModule.forRoot([ShareEffects, ProfileEffects, OrganizationsEffects, WorkspacesEffects]),
    StoreDevtoolsModule.instrument({
      maxAge: 25
    }),
    BrowserModule.withServerTransition({
      appId: 'nns-v1'
    }),
    HttpModule,
    JsonpModule,
    FormsModule,
    RouterModule,
    CommonModule,
    AppModule,
    OrigamiModule,
    BrowserTransferStateModule
  ],
  providers: [
    { provide: APP_BASE_HREF, useValue: '/' },
    { provide: 'isBrowser', useValue: true },
    { provide: 'isNode', useValue: false },
    Config,
    {
      deps: [Config],
      provide: SocketIoService,
      useClass: SocketIoService
    },
    {
      deps: [TransferState],
      provide: INITIAL_STATE,
      useFactory: getState
    },
    Meta,
    AddThisService,
  ],
  bootstrap: [AppComponent],
})
export class BrowserAppModule {
  constructor(
    public store: Store<IAppState>
  ) {
    const scrollingContainer = document.querySelector('nns-app')
    const scrollSource$ = observableFromEvent(scrollingContainer, 'scroll').pipe(
      map(() => detectIsBodyScrolled(scrollingContainer)),
      distinctUntilChanged(),
    );

    const scroll$ = scrollSource$
      .forEach((isBodyScrolled) => {
        const isHeaderFixed = detectIsHeaderFixed(isBodyScrolled);
        this.store.dispatch(setIsHeaderFixed(isHeaderFixed));
        this.store.dispatch(showToTopComponent(isBodyScrolled));
      });

    getResizeObservable(TABLET_WIDTH).subscribe((isTablet) => {
      const isBodyScrolled = detectIsBodyScrolled(scrollingContainer);
      const isHeaderFixed = detectIsHeaderFixed(isBodyScrolled);
      this.store.dispatch(setIsHeaderFixed(isHeaderFixed));
      this.store.dispatch(showToTopComponent(isBodyScrolled));
    });

    getResizeObservable(MOBILE_WIDTH).subscribe((mobile) => {
      const isBodyScrolled = detectIsBodyScrolled(scrollingContainer);
      const isHeaderFixed = detectIsHeaderFixed(isBodyScrolled);
      this.store.dispatch(setIsHeaderFixed(isHeaderFixed));
      this.store.dispatch(showToTopComponent(isBodyScrolled));
      this.store.dispatch(setMobile(mobile));
    });

    this.store.dispatch(setMobile(window.innerWidth <= MOBILE_WIDTH));

    observableFromEvent(window, 'resize').pipe(
      debounceTime(250),
      map((event: any) => ({width: event.target.innerWidth, height: event.target.innerHeight})),)
      .subscribe((({width, height}) => {
        this.store.dispatch(setWindowSize({width, height}));
      }));

    this.store.dispatch(setWindowSize({width: window.innerWidth, height: window.innerHeight}));

    const visual$ = this.store.select<IVisualState>('visual');
    visual$.forEach((visual) => {
      document.cookie = `nns-list-type=${visual.listType};path=/`;
      document.cookie = `nns-theme=${visual.theme};path=/`;
    });
  }
}

function detectIsHeaderFixed(isBodyScrolled) {
  return isBodyScrolled && (window.innerWidth > TABLET_WIDTH);
}

function detectIsBodyScrolled(customContainer: Element) {
  return ((window.pageYOffset
    || document.documentElement.scrollTop
    || document.body.scrollTop
    || customContainer.scrollTop
    || 0) >= HEADER_SCROLL_HEIGHT);
}

function getResizeObservable(width) {
  return observableFromEvent(window, 'resize').pipe(
    debounceTime(250),
    map((event: any) => (event.target.innerWidth <= width)),
    distinctUntilChanged(),
    catchError((err, caught) => caught),
  ) ;
}
